import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/compat/router';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useUI from 'src/hooks/use-ui';
import useCart from 'src/hooks/use-cart';
import useSearchPreferences from 'hooks/use-search-preferences';
import { useApolloNetworkStatus } from 'src/utils/apollo/network-status';

import useCarousel from 'hooks/use-carousel';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import {
  BrandCarousel,
  CategoryCarousel,
  CollectionCarousel,
  CustomCarousel,
  SpecialOffersCarousel,
  SpecialsCarousel,
  StaffPicksCarousel,
  SubcategoryCarousel,
  TopSellersCarousel,
} from '../carousels';

export const EmbeddedCarousel = observer(() => {
  const sectionRef = useRef(null);
  const { carousel } = useCarousel();
  const router = useRouter();
  const hideDiscounts = useHideDiscountFromConsumer();
  const { hideTitle, hideLink, routeRoot } = router?.query ?? {};
  const cart = useCart();
  const isCartEmpty = cart.isEmpty;
  const cartOrderType = cart.orderType;
  const SearchPreferences = useSearchPreferences();
  const orderTypePreference = SearchPreferences.orderType;
  const { numPendingQueries } = useApolloNetworkStatus();
  const { embeddedEventDispatcher } = useUI();
  const flags = useFlags();
  const isProductCardsV2Enabled = flags['growth.ecomm.product-card-v2.rollout'] ?? false;

  useEffect(() => {
    if (numPendingQueries > 0 || !isProductCardsV2Enabled || !sectionRef.current) {
      return;
    }

    const carouselHeight = sectionRef.current.offsetHeight;

    if (!carouselHeight) {
      return;
    }

    embeddedEventDispatcher(`embeddedCarousel:setHeight`, {
      carouselId: carousel?.id,
      height: carouselHeight,
    });
  }, [carousel?.id, embeddedEventDispatcher, isProductCardsV2Enabled, numPendingQueries]);

  if (!carousel) {
    return null;
  }

  const sectionProps = {
    section: carousel,
    showLink: _.toLower(hideLink) !== 'true',
    showTitle: _.toLower(hideTitle) !== 'true',
    routeRoot,
    isCartEmpty,
    cartOrderType,
    orderTypePreference,
  };

  function renderSection() {
    switch (carousel.sectionType) {
      case 'BRAND':
        return <BrandCarousel {...sectionProps} />;
      case 'CATEGORY':
        return <CategoryCarousel {...sectionProps} />;
      case 'COLLECTION':
        return <CollectionCarousel {...sectionProps} />;
      case 'CUSTOM':
        return <CustomCarousel {...sectionProps} />;
      case 'OFFERS':
        return <SpecialOffersCarousel {...sectionProps} />;
      case 'SPECIALS':
        return !hideDiscounts && <SpecialsCarousel {...sectionProps} />;
      case 'STAFF_PICKS':
        return <StaffPicksCarousel {...sectionProps} />;
      case 'SUBCATEGORY':
        return <SubcategoryCarousel {...sectionProps} />;
      case 'TOP_SELLERS':
        return <TopSellersCarousel {...sectionProps} />;
      default:
        return null;
    }
  }

  return <Sections ref={sectionRef}>{renderSection()}</Sections>;
});

const Sections = styled.div`
  margin: 0 25px;
`;
